import type { MentionNodeAttrs } from '@tiptap/extension-mention';
import {
  type SuggestionKeyDownProps,
  type SuggestionProps
} from '@tiptap/suggestion';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

export interface MentionListHandle {
  onKeyDown: (props: SuggestionKeyDownProps) => boolean;
}

export type Props = {
  ref: React.RefObject<MentionListHandle | null>;
} & SuggestionProps<
  {
    businessEntityColor: string;
    businessEntityIconName: string;
    businessEntityId: string;
    id: string;
    name: string;
  },
  {
    color: string;
    iconName: string;
  } & MentionNodeAttrs
>;

const MentionList = ({ command, items, ref }: Props) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedIndex(0);
  }, [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex(selectedIndex === 0 ? 0 : selectedIndex - 1);
        return true;
      }

      if (event.key === 'ArrowDown') {
        setSelectedIndex(
          selectedIndex === items.length - 1
            ? items.length - 1
            : selectedIndex + 1
        );
        return true;
      }

      if (event.key === 'PageUp') {
        setSelectedIndex(selectedIndex - 10 < 0 ? 0 : selectedIndex - 10);
        return true;
      }

      if (event.key === 'PageDown') {
        setSelectedIndex(
          selectedIndex + 10 > items.length - 1
            ? items.length - 1
            : selectedIndex + 10
        );
        return true;
      }

      if (event.key === 'Home') {
        setSelectedIndex(0);
        return true;
      }

      if (event.key === 'End') {
        setSelectedIndex(items.length - 1);
        return true;
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        const selectedItem = items[selectedIndex];
        command({
          color: selectedItem.businessEntityColor,
          iconName: selectedItem.businessEntityIconName,
          id: selectedItem.businessEntityId,
          label: selectedItem.name
        });
        return true;
      }

      return false;
    }
  }));

  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    itemRefs.current[selectedIndex]?.scrollIntoView({
      behavior: 'instant',
      block: 'nearest'
    });
  }, [selectedIndex]);

  return (
    <div className="bg-popover max-h-82.5 min-w-55 flex-col overflow-y-auto rounded-md border p-1 shadow-md">
      {items.length ? (
        items.map((item, index) => (
          <button
            className={cn(
              'hover:bg-accent flex w-full items-center gap-x-2 rounded-md px-2 py-1.5 text-sm',
              index === selectedIndex && 'bg-accent'
            )}
            key={item.id}
            onClick={() => {
              const selectedItem = items[index];
              command({
                color: selectedItem.businessEntityColor,
                iconName: selectedItem.businessEntityIconName,
                id: selectedItem.businessEntityId,
                label: selectedItem.name
              });
            }}
            ref={element => {
              itemRefs.current[index] = element;
            }}
            type="button"
          >
            {item.name}
            <span className="ml-auto text-xs opacity-60">
              @
              {items[index].businessEntityId
                .split('_')
                .map(a => `${a[0].toUpperCase()}${a.slice(1)}`)
                .join('-')}
            </span>
          </button>
        ))
      ) : (
        <div className="w-full rounded-md px-2 py-1.5 text-sm">No result</div>
      )}
    </div>
  );
};

export default MentionList;
